import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzSelectModule } from 'ng-zorro-antd/select';
import {
  NzAutocompleteModule,
  NzOptionSelectionChange
} from 'ng-zorro-antd/auto-complete';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { LiteJsonEditorComponent } from '@care-portals/ui';
import {
  CreateSubscriptionDTO,
  IProductV2
} from '@care-portals/api-interfaces';
import { SubscriptionsFacade } from '../../../+store/subscriptions/subscriptions.facade';
import { ProductFacade } from '../../../+store/product/product.facade';
import { takeWhile } from 'rxjs';
import { isValid } from 'date-fns';
import {
  getPhaseByCycle,
  normalizeCurrency,
  validateCurrentCycle
} from '@care-portals/shared';

@Component({
  selector: 'portals-emr-create-subscription',
  templateUrl: './create-subscription.component.html',
  styleUrls: ['./create-subscription.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzFormModule,
    NzAutocompleteModule,
    NzSelectModule,
    NzInputNumberModule,
    NzDatePickerModule,
    NzButtonModule,
    NzInputModule,
    LiteJsonEditorComponent
  ]
})
export class CreateSubscriptionComponent implements OnInit, OnDestroy {
  @Input() customerId: string;

  form: FormGroup;
  subscriptionProducts: IProductV2[] = [];
  filteredProducts: IProductV2[] = [];
  keyword = '';
  private selectedProductId: string | null = null;
  isLoadingProducts = false;
  isSubmitting = false;
  private active = true;

  constructor(
    private readonly fb: FormBuilder,
    private readonly nzDrawerRef: NzDrawerRef,
    private readonly subscriptionsFacade: SubscriptionsFacade,
    private readonly productFacade: ProductFacade
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      product: [null, Validators.required],
      productSearch: [''],
      currentCycle: [1, [this.checkCurrentCycle.bind(this)]],
      nextCycleDate: [null, Validators.required],
      extras: [{}]
    });

    this.form
      .get('product')
      ?.valueChanges.pipe(takeWhile(() => this.active))
      .subscribe(() => {
        this.form.get('currentCycle')?.updateValueAndValidity();
      });

    this.productFacade.loadProducts();
    this.productFacade.products$
      .pipe(takeWhile(() => this.active))
      .subscribe(products => {
        this.subscriptionProducts = (products || []).filter(
          p => p.isSubscription === true
        );
        const normalized = this.keyword.trim().toLowerCase();
        this.filteredProducts = normalized
          ? this.subscriptionProducts.filter(
            product =>
              product.label?.toLowerCase().includes(normalized) ||
              (product.subLabel?.toLowerCase().includes(normalized) ?? false)
          )
          : this.subscriptionProducts;
      });
    this.productFacade.loading$
      .pipe(takeWhile(() => this.active))
      .subscribe(loading => (this.isLoadingProducts = loading));
    this.subscriptionsFacade.loading$
      .pipe(takeWhile(() => this.active))
      .subscribe(loading => (this.isSubmitting = loading));
  }

  ngOnDestroy(): void {
    this.active = false;
  }

  close(): void {
    this.nzDrawerRef.close();
  }

  submit(): void {
    if (this.form.invalid || !this.customerId) {
      Object.values(this.form.controls).forEach(c => {
        c.markAsDirty();
        c.updateValueAndValidity();
      });
      return;
    }

    const { product, currentCycle, nextCycleDate, extras } = this.form.value;
    const extrasObj =
      extras && typeof extras === 'object' && !Array.isArray(extras)
        ? extras
        : {};

    const payload: Partial<CreateSubscriptionDTO> = {
      customer: this.customerId,
      product,
      nextCycleDate: new Date(nextCycleDate.setHours(23, 59, 59, 999)),
      currentCycle: currentCycle ?? 1,
      ...(Object.keys(extrasObj).length ? { extras: extrasObj } : {})
    };
    this.subscriptionsFacade.createSubscription(payload);
  }

  get currentCycleErrorTip(): string {
    const control = this.form?.get('currentCycle');
    const err = control?.errors;
    if (!control || !err) return '';
    if (err['required']) return 'Required';
    if (err['min']) return 'Min 1';
    if (err['currentCycle']) return err['currentCycle'];
    return '';
  }

  get nextCycleChargeMessage(): string | null {
    const productId = this.form?.value?.product;
    const selectedProduct = this.subscriptionProducts.find(p => p._id === productId);
    if (!selectedProduct?.subscriptionPhases?.length) {
      return null;
    }

    const currentCycleRaw = this.form?.value?.currentCycle;
    const currentCycle = Number(currentCycleRaw);
    if (!Number.isFinite(currentCycle) || currentCycle < 1) {
      return null;
    }

    const nextCycle = currentCycle + 1;
    const nextPhase = getPhaseByCycle(selectedProduct.subscriptionPhases, nextCycle);
    if (!nextPhase || nextPhase.price == null) {
      return null;
    }

    const currency = normalizeCurrency(selectedProduct.currency) || 'USD';
    const formattedPrice = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(nextPhase.price);

    return `Customer will be charged ${formattedPrice} in the next cycle`;
  }

  checkCurrentCycle(control: AbstractControl): ValidationErrors | null {
    if (
      this.subscriptionProducts.length === 0 ||
      this.form.value.product == null
    ) {
      return null;
    }
    const product = this.subscriptionProducts.find(
      p => p._id === this.form.value.product
    );
    if (!product) {
      return null;
    }

    const validatedCurrentCycle = validateCurrentCycle(
      product.subscriptionPhases,
      control.value
    );
    if (validatedCurrentCycle?.error) {
      return { currentCycle: validatedCurrentCycle.error };
    }

    return null;
  }

  disablePastAndTodayDates = (current: Date): boolean => {
    if (!current || !isValid(current)) {
      return false;
    }

    const currentDateAtStartOfDay = new Date(current);
    currentDateAtStartOfDay.setHours(0, 0, 0, 0);

    const todayAtStartOfDay = new Date();
    todayAtStartOfDay.setHours(0, 0, 0, 0);

    return currentDateAtStartOfDay.getTime() <= todayAtStartOfDay.getTime();
  };

  onChange(e: Event): void {
    const value = (e.target as HTMLInputElement).value ?? '';
    this.keyword = value;

    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      this.filteredProducts = this.subscriptionProducts;
      this.form.get('product')?.setValue(null);
      this.form.get('product')?.updateValueAndValidity();
      this.selectedProductId = null;
      return;
    }

    // Always search across BOTH label and subLabel.
    this.filteredProducts = this.subscriptionProducts.filter(
      product =>
        product.label?.toLowerCase().includes(normalized) ||
        (product.subLabel?.toLowerCase().includes(normalized) ?? false)
    );
  }

  fieldSelected(event: NzOptionSelectionChange, product: IProductV2): void {
    if (!event?.isUserInput) return;

    this.selectedProductId = product?._id ?? null;

    this.form.get('product')?.setValue(this.selectedProductId);
    this.form.get('product')?.markAsDirty();
    this.form.get('product')?.updateValueAndValidity();

    // Keep the input showing the selected product label.
    this.keyword = product?.label ?? '';
  }
}
